import MainInformation from '@/components/MainInformation';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { FC, useEffect, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { FileData } from '@/libs/fleet-api/services/types';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import {
	getFRMCallReportDetail,
	submitFRMCallReport,
	SubmitFRMCallReportPayload,
} from '@/libs/fleet-api/services/visit-reports';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import FleetModal from '@/libs/antd/components/Modal';
import { useSelectConfirmationMedia } from '@/libs/fleet-api/hooks/master-data';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { PageName, useApp } from '@/App';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type CallReportForm = Omit<SubmitFRMCallReportPayload, 'confirmationMedia'> & {
	confirmationMedia: MasterData;
};

const FRMCallReportPage: FC<{ prospectId: string; redirect?: PageName }> = ({
	prospectId,
	redirect,
}) => {
	const { navigate } = useApp();
	const { modal } = useModals();

	const { form, value, fieldName } = useForm<CallReportForm>();
	const { surveyCallContacts, surveyCallConfirmations } = value ?? {};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const surveyCallContactModal = useSurveyCallContactModal();
	const surveyCallConfirmationModal = useSurveyCallConfirmationModal();

	const selectConfirmationMedia = useSelectConfirmationMedia();

	const submitFRMCallReportMutation = useMutation({
		mutationFn: submitFRMCallReport,
	});

	const { data, isFetching, isError } = useQuery({
		queryKey: ['getFRMVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getFRMCallReportDetail(id);
		},
	});

	const { mainInformation, customerBusinessDocuments, customerLegalDocuments } =
		data?.data ?? {};

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<MainInformation
				mainInformation={mainInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				isLoading={isFetching}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					const isConfirmed = await modal.confirm({
						title:
							'Apakah Anda yakin ingin menyimpan data ini? Pastikan semua informasi sudah benar sebelum melanjutkan',
						okText: 'YA',
						cancelText: 'TIDAK',
					});
					if (!isConfirmed) return;

					try {
						await submitFRMCallReportMutation.mutateAsync({
							prospectId,
							confirmationDate: values.confirmationDate,
							confirmationMedia: values.confirmationMedia?.id,
							supportingDocument: values.supportingDocument?.id
								? {
										id: values.supportingDocument.id,
										name: values.supportingDocument.name,
										path: values.supportingDocument.path,
									}
								: undefined!,
							surveyCallConfirmations: values.surveyCallConfirmations,
							surveyCallContacts: values.surveyCallContacts,
							additionalInformation: values.additionalInformation,
						});
						await modal.success({
							title: 'Call Report Anda berhasil tersimpan',
							okText: 'BACK',
						});

						navigate(redirect ?? 'FRMVisitReportListPage');
					} catch (error) {
						console.log('error', error);
					}
				}}
			>
				<Form.Item
					name={fieldName('surveyCallContacts')}
					initialValue={[]}
					noStyle
				/>
				<Form.Item
					name={fieldName('surveyCallConfirmations')}
					initialValue={[]}
					noStyle
				/>
				<Space
					direction="vertical"
					style={{ display: 'flex' }}
					size={'large'}
				>
					<FleetTitle>Laporan Periodic Call - Konfirmasi</FleetTitle>
					<Row gutter={[40, 20]}>
						<Col span={12}>
							<Form.Item
								label="Tanggal Konfirmasi"
								name={fieldName('confirmationDate')}
								rules={[rule.required()]}
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								labelCol={{
									span: 8,
								}}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
							>
								<DatePicker />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Media Konfirmasi"
								rules={[rule.required()]}
								name={fieldName('confirmationMedia')}
								getValueFromEvent={(_, option) => option?.data}
								getValueProps={(value) => {
									if (!value) return {};
									return {
										value: {
											value: value.id,
											label: value.value,
										},
									};
								}}
							>
								<Select {...selectConfirmationMedia} />
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					<Row align="middle">
						<Col flex="auto">
							<Typography.Title level={3}>
								Pihak Yang Dihubungi
							</Typography.Title>
						</Col>
						<Col>
							<Button
								style={{
									color: '#1557BC',
									borderRadius: 50,
									fontSize: 12,
									height: 24,
									borderColor: '#1557BC',
									fontWeight: 700,
									marginBottom: 20,
								}}
								icon={'+'}
								onClick={() => {
									surveyCallContactModal.open({ type: 'new' });
								}}
							>
								Add Another
							</Button>
						</Col>
					</Row>

					<Table
						bordered
						pagination={false}
						dataSource={
							surveyCallContacts?.map((data, key) => {
								return {
									...data,
									key,
								};
							}) ?? []
						}
						columns={[
							{
								title: 'Action',
								align: 'center',
								render: (_, data, index) => {
									return (
										<Row
											gutter={[40, 20]}
											style={{
												display: 'flex',
												flexWrap: 'nowrap',
												justifyContent: 'center',
												paddingLeft: 20,
												paddingRight: 20,
											}}
										>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
													width: 'fit-content',
												}}
											>
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														surveyCallContactModal.open({
															type: 'edit',
															data,
															index,
														});
													}}
												>
													<EditOutlined />
												</a>
											</div>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
												}}
											>
												<Divider type="vertical" />
											</div>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
													width: 'fit-content',
												}}
											>
												<a
													style={{ display: 'block' }}
													onClick={async (e) => {
														e.preventDefault();
														const confirmDelete = await modal.confirm({
															title: 'Hapus data ini?',
														});
														if (!confirmDelete) return;

														const { surveyCallContacts } =
															form.getFieldsValue();

														form.setFieldValue(
															fieldName('surveyCallContacts'),
															surveyCallContacts.filter((_, i) => i !== index),
														);
													}}
												>
													<DeleteOutlined />
												</a>
											</div>
										</Row>
									);
								},
								width: 1,
							},
							{
								title: 'Nama',
								dataIndex: 'name',
							},
							{
								title: 'Jabatan',
								dataIndex: 'position',
							},
							{
								title: 'Nama Perusahaan',
								dataIndex: 'companyName',
							},
							{
								title: 'Nomor Telepon',
								dataIndex: 'phoneNumber',
							},
						]}
					/>

					<Row align="middle">
						<Col flex="auto">
							<Typography.Title level={3}>Hasil Konfirmasi</Typography.Title>
						</Col>
						<Col>
							<Button
								style={{
									color: '#1557BC',
									borderRadius: 50,
									fontSize: 12,
									height: 24,
									borderColor: '#1557BC',
									fontWeight: 700,
									marginBottom: 20,
								}}
								icon={'+'}
								onClick={() => {
									surveyCallConfirmationModal.open({ type: 'new' });
								}}
							>
								Add Another
							</Button>
						</Col>
					</Row>

					<Table
						bordered
						pagination={false}
						dataSource={
							surveyCallConfirmations?.map((data, key) => {
								return {
									...data,
									key,
								};
							}) ?? []
						}
						columns={[
							{
								title: 'Action',
								align: 'center',
								render: (_, data, index) => {
									return (
										<Row
											gutter={[40, 20]}
											style={{
												display: 'flex',
												flexWrap: 'nowrap',
												justifyContent: 'center',
												paddingLeft: 20,
												paddingRight: 20,
											}}
										>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
													width: 'fit-content',
												}}
											>
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														surveyCallConfirmationModal.open({
															type: 'edit',
															index,
															data,
														});
													}}
												>
													<EditOutlined />
												</a>
											</div>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
												}}
											>
												<Divider type="vertical" />
											</div>
											<div
												style={{
													display: 'flex',
													flexWrap: 'nowrap',
													justifyContent: 'center',
													width: 'fit-content',
												}}
											>
												<a
													style={{ display: 'block' }}
													onClick={async (e) => {
														e.preventDefault();
														const confirmDelete = await modal.confirm({
															title: 'Hapus data ini?',
														});

														if (!confirmDelete) return;

														const { surveyCallConfirmations } =
															form.getFieldsValue();

														form.setFieldValue(
															fieldName('surveyCallConfirmations'),
															surveyCallConfirmations.filter(
																(_, i) => i !== index,
															),
														);
													}}
												>
													<DeleteOutlined />
												</a>
											</div>
										</Row>
									);
								},
								width: 1,
							},
							{
								title: 'Poin Konfirmasi',
								dataIndex: 'confirmationPoint',
							},
							{
								title: 'Detail Konfirmasi',
								dataIndex: 'confirmationDetail',
							},
						]}
					/>

					<Row>
						<Col span={12}>
							<Form.Item
								name={fieldName('supportingDocument')}
								label="Upload Foto"
								labelCol={{ span: 10 }}
								rules={[rule.required()]}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									accept=".jpg,.jpeg,.png"
									maxCount={1}
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(fieldName('supportingDocument'), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${'customerId'}/${prospectId}/site-observation-photos`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});
											form.setFieldValue(fieldName('supportingDocument'), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName('supportingDocument'), null);
										}
									}}
								>
									{uploadMutation.isPending ? (
										<LoadingOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									) : (
										<UploadOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									)}
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>

					<Alert
						message="Evidence dapat berupa screenshoot dari panggilan Zoom atau Whatsapp"
						type="info"
						showIcon
					/>

					<Button
						type="primary"
						htmlType="submit"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
							marginTop: 40,
						}}
						loading={submitFRMCallReportMutation.isPending}
						disabled={submitFRMCallReportMutation.isPending}
					>
						NEXT
					</Button>
				</Space>
			</Form>

			<SurveyCallContactModal
				{...surveyCallContactModal}
				onSubmit={(values, index) => {
					const { surveyCallContacts } = form.getFieldsValue();

					if (typeof index === 'number') {
						form.setFieldValue(
							fieldName('surveyCallContacts'),
							surveyCallContacts.map((data, i) => {
								return i === index ? values : data;
							}),
						);
					} else {
						form.setFieldValue(fieldName('surveyCallContacts'), [
							...surveyCallContacts,
							values,
						]);
					}
				}}
			/>

			<SurveyCallConfirmationModal
				{...surveyCallConfirmationModal}
				onSubmit={(values, index) => {
					const { surveyCallConfirmations } = form.getFieldsValue();

					if (typeof index === 'number') {
						form.setFieldValue(
							fieldName('surveyCallConfirmations'),
							surveyCallConfirmations.map((data, i) => {
								return i === index ? values : data;
							}),
						);
					} else {
						form.setFieldValue(fieldName('surveyCallConfirmations'), [
							...surveyCallConfirmations,
							values,
						]);
					}
				}}
			/>
		</Fragment>
	);
};

export default FRMCallReportPage;

type SurveyCallContact =
	SubmitFRMCallReportPayload['surveyCallContacts'][number];

const useSurveyCallContactModal = () => {
	const [modalData, setModalData] = useState<
		| {
				type: 'new';
		  }
		| {
				type: 'edit';
				index: number;
				data: SurveyCallContact;
		  }
		| null
	>(null);

	return {
		data: modalData,
		open: (data: typeof modalData) => setModalData(data),
		close: () => setModalData(null),
	};
};

type SurveyCallContactModalProps = ReturnType<
	typeof useSurveyCallContactModal
> & {
	onSubmit: (data: SurveyCallContact, index?: number) => void;
};

const SurveyCallContactModal: FC<SurveyCallContactModalProps> = (props) => {
	const { form, fieldName } = useForm<SurveyCallContact>();

	useEffect(() => {
		if (props.data?.type === 'edit') {
			form.setFieldsValue(props.data.data);
		}
	}, [props.data, form]);

	return (
		<FleetModal
			open={Boolean(props.data)}
			closable={false}
			maskClosable={false}
			width={940}
			okText="SAVE"
			cancelText="BACK"
			destroyOnClose
			buttonPosition="end"
			onOk={form.submit}
			onCancel={props.close}
			afterOpenChange={(isOpen) => {
				if (!isOpen) form.resetFields();
			}}
		>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<FleetTitle>Add Pihak yang Dihubungi</FleetTitle>

				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={(values) => {
						props.onSubmit(
							values,
							props.data?.type === 'edit' ? props.data.index : undefined,
						);
						props.close();
					}}
				>
					{/* {props.data?.type === 'edit' && (
						<Fragment>
							<Form.Item
								name={fieldName('id')}
								noStyle
							/>
							<Form.Item
								name={fieldName('surveyId')}
								noStyle
							/>
						</Fragment>
					)} */}
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('name')}
								label="Nama"
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('position')}
								label="Jabatan"
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('companyName')}
								label="Nama Perusahaan"
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('phoneNumber')}
								label="No.Telephone"
								rules={[rule.required(), rule.phoneNumber()]}
							>
								<Input maxLength={13} />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
				</Form>
			</Space>
		</FleetModal>
	);
};

type SurveyCallConfirmation =
	SubmitFRMCallReportPayload['surveyCallConfirmations'][number];

const useSurveyCallConfirmationModal = () => {
	const [modalData, setModalData] = useState<
		| {
				type: 'new';
		  }
		| {
				type: 'edit';
				index: number;
				data: SurveyCallConfirmation;
		  }
		| null
	>(null);

	return {
		data: modalData,
		open: (data: typeof modalData) => setModalData(data),
		close: () => setModalData(null),
	};
};

type SurveyCallConfirmationModalProps = ReturnType<
	typeof useSurveyCallConfirmationModal
> & {
	onSubmit: (data: SurveyCallConfirmation, index?: number) => void;
};

const SurveyCallConfirmationModal: FC<SurveyCallConfirmationModalProps> = (
	props,
) => {
	const { form, fieldName } = useForm<SurveyCallConfirmation>();

	useEffect(() => {
		if (props.data?.type === 'edit') {
			form.setFieldsValue(props.data.data);
		}
	}, [props.data, form]);

	return (
		<FleetModal
			open={Boolean(props.data)}
			closable={false}
			maskClosable={false}
			width={940}
			okText="SAVE"
			cancelText="BACK"
			destroyOnClose
			buttonPosition="end"
			onOk={form.submit}
			onCancel={props.close}
			afterOpenChange={(isOpen) => {
				if (!isOpen) form.resetFields();
			}}
		>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<FleetTitle>Add Tujuan Konfirmasi</FleetTitle>

				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={(values) => {
						props.onSubmit(
							values,
							props.data?.type === 'edit' ? props.data.index : undefined,
						);
						props.close();
					}}
				>
					{/* {props.data?.type === 'edit' && (
						<Fragment>
							<Form.Item
								name={fieldName('id')}
								noStyle
							/>
						</Fragment>
					)} */}
					<Row>
						<Col span={12}>
							<Form.Item
								name={fieldName('confirmationPoint')}
								label="Poin Konfirmasi"
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row>
						<Col span={24}>
							<Form.Item
								name={fieldName('confirmationDetail')}
								label="Detail Konfirmasi"
								rules={[rule.required()]}
								labelCol={{
									span: 6,
								}}
							>
								<Input.TextArea rows={3} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Space>
		</FleetModal>
	);
};
